

// react-routers components
import React, { useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import Link  from "@mui/material/Link";
import { useUpdateUserMutation } from "context/users/usersApiSlice";
import DOMPurify from 'isomorphic-dompurify';
import parse from 'html-react-parser';
// prop-types is library for typechecking of props
import PropTypes from "prop-types";

// @mui material components
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import Tooltip from "@mui/material/Tooltip";
import Icon from "@mui/material/Icon";
import CircularProgress from '@mui/material/CircularProgress';
import { green } from '@mui/material/colors';
import Fab from '@mui/material/Fab';
import { InputAdornment } from "@mui/material";

// Material Dashboard 2 React components
import MDBox from "dashboard/components/MDBox";
import MDTypography from "dashboard/components/MDTypography";
import MDEditor from "dashboard/components/MDEditor";
// Material Dashboard 2 React base styles
import colors from "assets/theme/base/colors";
import typography from "assets/theme/base/typography";
import MDInput from 'dashboard/components/MDInput';
import MDButton from "dashboard/components/MDButton";
import MDAlert from "dashboard/components/MDAlert";
import useAlert from 'hooks/useAlert';

function ProfileInfoCard({ title, description, info, social, action, shadow }) {

  const [updateProfile, {isLoading, isError, error}] = useUpdateUserMutation();
  const {showAlerts} = useAlert();
  const labels = [];
  const values = [];
  const { socialMediaColors } = colors;
  const { size } = typography;

  const initValues = {
    bioshow : false,
    socialshow :false,
    submition : false,
    bio : description === 'Parlez-nous de vous!' ? '' : description,
    social : {
      fb : social[0].link,
      insta : social[2].link,
      tweet : social[1].link,
      ytube : social[3].link
    },
    success : false,
  }
  const [formData, setFormData] = useState(initValues);

  // Convert this form `objectKey` of the object key in to this `object key`
  Object.keys(info).forEach((el) => {
    if (el.match(/[A-Z\s]+/)) {
      const uppercaseLetter = Array.from(el).find((i) => i.match(/[A-Z]+/));
      const newElement = el.replace(uppercaseLetter, ` ${uppercaseLetter.toLowerCase()}`);
      labels.push(newElement);
    } else {
      labels.push(el);
    }
  });

  const handleEditorChange = value =>{
    setFormData({
      ...formData,
      bio : value
    })
  }

  const handleSMChange = event =>{
    setFormData({
      ...formData,
      social : {
        ...formData.social,
        [event.target.name] : event.target.value
      }
    })
  }

  const handleSubmit = async event => {
    event.preventDefault();
    setFormData({
      ...formData,
      submition : true
    });
    const {bioshow, submition, success,socialshow,...rest} = formData
      try{
        const userData = await updateProfile({filter: {id:action.route}, update:rest}).unwrap();
        showAlerts({code:"success", msg : userData.message, title: "Profile update " ,type : "toast"});
        setFormData({
          ...formData,
          success : true
        });
      } catch(err) {
        showAlerts({code:"error", msg : err.error, title: "Profile update " ,type : "toast"});
      }
  }

  const buttonSx = {
    ...(formData.success && {
      bgcolor: green[500],
      '&:hover': {
        bgcolor: green[700],
      },
    }),
  };

  // Push the object values into the values array
  Object.values(info).forEach((el) => values.push(el));

  // Render the card info items
  const renderItems = labels.map((label, key) => (
    <MDBox key={label} display="flex"  py={1} pr={2}>
      <MDTypography variant="button" fontWeight="bold" textTransform="capitalize">
        {label}: &nbsp;
      </MDTypography>
      <MDTypography variant="button" fontWeight="regular" color="text">
        &nbsp;{values[key]}
      </MDTypography>
    </MDBox>
  ));

  // Render the card social media icons
  const renderSocial = social.map(({ link, icon, color }) => (
    <MDBox
      key={color}
      component="a"
      href={link}
      target="_blank"
      rel="noreferrer"
      fontSize={size.lg}
      color={socialMediaColors[color].main}
      pr={1}
      pl={0.5}
      lineHeight={1}
    >
      {icon}
    </MDBox>
  ));

  return (
    <Card sx={{ height: "100%", width:"100%", boxShadow: !shadow && "none" }}>
      <MDBox display="flex" justifyContent="space-between" alignItems="center" pt={2} px={2}>
        <MDTypography variant="h6" fontWeight="medium" textTransform="capitalize">
          {title}
        </MDTypography>
        <MDTypography  variant="body2" color="secondary">
          <Tooltip title={action.tooltip} placement="top">
            <MDButton onClick={()=>setFormData({...initValues, bioshow : !formData.bioshow})} iconOnly><Icon>edit</Icon></MDButton>
          </Tooltip>
        </MDTypography>
      </MDBox>
      <MDBox p={2}>
        <MDBox mb={2} lineHeight={1} >
            {formData.bioshow ? 
              <MDBox component="form" sx={{ whiteSpace: "normal" }} role="form" onSubmit={handleSubmit} 
                noValidate autoComplete="on">
                    <MDEditor  value={formData.bio}
                    placeholder="Parlez-nous de vous !" 
                    onChange={handleEditorChange}
                    error={formData.submition && !formData.bio}
                    helperText={formData.submition && !formData.bio ? "please fill the field before saving" : null}
                    fullWidth/>
                <MDBox m={1} textAlign="right" sx={{ m: .2, position: 'relative' }} >
                    <Fab
                      aria-label="save"
                      color="info"
                      sx={buttonSx}
                      onClick={handleSubmit}
                      size="small"
                    >
                      <Icon fontSize="small" >{formData.success ? "done" : "save"}</Icon>
                    </Fab>
                    {isLoading && (
                      <CircularProgress
                        size={40}
                        sx={{
                          color:"white !important",
                          position: 'absolute',
                          top: 0,
                          right: 0,
                          zIndex: 1,
                          opacity:.6
                        }}
                      />
                    )}
                </MDBox>
                {isError  && <MDAlert color="error"><small style={{ fontSize:"12px",whiteSpace: "normal" }}> {error?.data?.error} </small></MDAlert> }
              </MDBox>
          : <MDTypography component="div" variant="caption" sx={{fontSize:14}} color="text" fontWeight="light">
            {parse(DOMPurify.sanitize(description))}
          </MDTypography>}
        </MDBox>
        <MDBox opacity={0.3}>
          <Divider />
        </MDBox>
        <MDBox>
          {renderItems}
          <MDBox display="flex" sx={{alignItems:"center"}} py={1} pr={2}>
            <MDTypography variant="button" fontWeight="bold" textTransform="capitalize">
              social: &nbsp;
            </MDTypography>
            {formData.socialshow ? 
              <MDBox component="form" sx={{ whiteSpace: "normal" }} p={{xs:.5, lg : 1}} role="form" onSubmit={handleSubmit} 
                noValidate autoComplete="on">
                <MDInput multiline  name="fb" value={formData.social.fb}
                  placeholder="Enter your facebook profile link" 
                  onChange={handleSMChange} size="small" sx={{mb:1}}
                  InputProps={{
                    startAdornment: (
                        <InputAdornment sx={{ml:1 , color:`#344767 !important`}} position="start">
                             {social[0].icon}
                        </InputAdornment>
                  )}}
                  fullWidth />
                <MDInput multiline  name="insta" value={formData.social.insta}
                  placeholder="Enter your instagram profile link" 
                  onChange={handleSMChange} size="small" sx={{mb:1}}
                  InputProps={{
                    startAdornment: (
                        <InputAdornment sx={{ml:1 , color:"#125688"}} position="start">
                            {social[2].icon}
                        </InputAdornment>
                  )}}
                  fullWidth />
                <MDInput multiline  name="tweet" value={formData.social.tweet}
                  placeholder="Enter your twitter profile link" 
                  onChange={handleSMChange} size="small" sx={{mb:1}}
                  InputProps={{
                    startAdornment: (
                        <InputAdornment sx={{ml:1, color:"#55acee"}} position="start">
                             {social[1].icon}
                        </InputAdornment>
                  )}}
                  fullWidth />
                <MDInput multiline  name="ytube" value={formData.social.ytube}
                  placeholder="Enter your youtube profile link" 
                  onChange={handleSMChange} size="small"
                  InputProps={{
                    startAdornment: (
                        <InputAdornment sx={{ml:1, color:"#e52d27"  }} position="start">
                             {social[3].icon}
                        </InputAdornment>
                  )}}
                  fullWidth />
                <MDBox m={1} textAlign="right" sx={{ m: .2, position: 'relative' }} >
                    <Fab
                      aria-label="save"
                      color="info"
                      sx={buttonSx}
                      onClick={handleSubmit}
                      size="small"
                    >
                      <Icon fontSize="small" >{formData.success ? "done" : "save"}</Icon>
                    </Fab>
                    {isLoading && (
                      <CircularProgress
                        size={40}
                        sx={{
                          color:"white !important",
                          position: 'absolute',
                          top: 0,
                          right: 0,
                          zIndex: 1,
                          opacity:.6
                        }}
                      />
                    )}
                </MDBox>
                {isError  && <MDAlert color="error"><small style={{ fontSize:"12px",whiteSpace: "normal" }}> {error?.data?.error} </small></MDAlert> }
              </MDBox>
            : renderSocial}
            <Tooltip title="Edit social account" placement="top">
              <MDButton onClick={()=>setFormData({...initValues, socialshow : !formData.socialshow})} iconOnly><Icon>edit</Icon></MDButton>
            </Tooltip>

            <MDBox display="flex" alignItems="center" mb={0.5} ml={-1.5}>
              <Divider sx={{ml: 3, mr:1, height:24, opacity:1}}  orientation="vertical" />
              <MDBox mt={0.5}>
                <Icon fontSize="xl" sx={{ mx:1 , color:({palette}) => palette.text }}>key</Icon>
              </MDBox>
              <MDBox width="80%" ml={0.5}>
                  <MDTypography variant="button" fontWeight="regular" color="text">
                  <Link sx={{  transition :"all ease-in-out .5s", '&:hover':{
                          color : ({palette}) => palette.info.dark,
                          textDecoration: 'underline'
                    }}} underline="none" component={RouterLink} to='/authentication/NewPass'>Change the password</Link>
                    
                  </MDTypography>
              </MDBox>
            </MDBox>
          </MDBox>
        </MDBox>
      </MDBox>
    </Card>
  );
}

// Setting default props for the ProfileInfoCard
ProfileInfoCard.defaultProps = {
  shadow: true,
  action : {}
};

// Typechecking props for the ProfileInfoCard
ProfileInfoCard.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  info: PropTypes.objectOf(PropTypes.string).isRequired,
  social: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  action: PropTypes.shape({
    route: PropTypes.string.isRequired,
    tooltip: PropTypes.string.isRequired,
  }).isRequired,
  shadow: PropTypes.bool,
};

export default ProfileInfoCard;
